import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  Clock, Plus, Edit2, Trash2, Shield, Archive, AlertTriangle,
  FileText, Lock, Unlock, RefreshCw, Loader2, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_CATEGORIES = [
  { value: 'BILANCIO_PREVENTIVO', label: 'Bilancio Preventivo' },
  { value: 'BILANCIO_CONSUNTIVO', label: 'Bilancio Consuntivo' },
  { value: 'FATTURE', label: 'Fatture' },
  { value: 'CONTRATTI', label: 'Contratti' },
  { value: 'VERBALI', label: 'Verbali Assemblee' },
  { value: 'CONVOCAZIONI', label: 'Convocazioni' },
  { value: 'TABELLE_MILLESIMALI', label: 'Tabelle Millesimali' },
  { value: 'POLIZZE', label: 'Polizze Assicurative' },
  { value: 'COMUNICAZIONI', label: 'Comunicazioni' },
  { value: 'ALTRO', label: 'Altri Documenti' },
];

const RETENTION_ACTIONS = [
  { value: 'ARCHIVE', label: 'Archivia', description: 'Sposta in archivio storico' },
  { value: 'DELETE', label: 'Elimina', description: 'Elimina definitivamente' },
  { value: 'ANONYMIZE', label: 'Anonimizza', description: 'Rimuovi dati personali' },
];

export default function RetentionSettings() {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  
  const [policies, setPolicies] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  
  // Policy form
  const [policyForm, setPolicyForm] = useState({
    category: '',
    retention_years: '',
    retention_days: '',
    action_on_expiry: 'ARCHIVE',
    legal_hold_supported: true,
    description: ''
  });

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      // Fetch policies
      const policiesRes = await fetch(`${API_URL}/api/compliance/retention-policies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (policiesRes.ok) {
        const data = await policiesRes.json();
        setPolicies(data.policies || []);
      }
      
      // Fetch expiring documents if condominium selected
      if (condoId) {
        const [docsRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/compliance/documents/expiring?condominium_id=${condoId}&days_ahead=60`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/compliance/retention-stats?condominium_id=${condoId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        if (docsRes.ok) {
          const data = await docsRes.json();
          setExpiringDocs(data.documents || []);
        }
        
        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [token, condoId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const seedDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/compliance/retention-policies/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        toast.error('Errore nella creazione policy predefinite');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const resetPolicyForm = () => {
    setPolicyForm({
      category: '',
      retention_years: '',
      retention_days: '',
      action_on_expiry: 'ARCHIVE',
      legal_hold_supported: true,
      description: ''
    });
    setEditingPolicy(null);
  };

  const openNewPolicyDialog = () => {
    resetPolicyForm();
    setShowPolicyDialog(true);
  };

  const openEditPolicyDialog = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      category: policy.category,
      retention_years: policy.retention_years?.toString() || '',
      retention_days: policy.retention_days?.toString() || '',
      action_on_expiry: policy.action_on_expiry || 'ARCHIVE',
      legal_hold_supported: policy.legal_hold_supported !== false,
      description: policy.description || ''
    });
    setShowPolicyDialog(true);
  };

  const handleSavePolicy = async () => {
    if (!policyForm.category) {
      toast.error('Seleziona una categoria');
      return;
    }
    
    if (!policyForm.retention_years && !policyForm.retention_days) {
      toast.error('Specifica un periodo di conservazione');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        category: policyForm.category,
        retention_years: policyForm.retention_years ? parseInt(policyForm.retention_years) : null,
        retention_days: policyForm.retention_days ? parseInt(policyForm.retention_days) : null,
        action_on_expiry: policyForm.action_on_expiry,
        legal_hold_supported: policyForm.legal_hold_supported,
        description: policyForm.description || null
      };
      
      const res = await fetch(`${API_URL}/api/compliance/retention-policies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Policy salvata');
        setShowPolicyDialog(false);
        resetPolicyForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Eliminare questa policy?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/compliance/retention-policies/${policyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Policy eliminata');
        fetchData();
      } else {
        toast.error('Errore nella cancellazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleBulkLegalHold = async (enable) => {
    if (selectedDocs.length === 0) {
      toast.error('Seleziona almeno un documento');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/compliance/documents/bulk-legal-hold?enable=${enable}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(selectedDocs)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setSelectedDocs([]);
        fetchData();
      } else {
        toast.error('Errore nell\'applicazione del legal hold');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const getCategoryLabel = (value) => {
    return DOCUMENT_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Conservazione dati"
        subtitle="Politiche di retention (GDPR)"
        actions={
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total_documents || 0}</p>
                <p className="text-sm text-gray-500">Totale Documenti</p>
              </div>
              <FileText className="h-8 w-8 text-gray-300" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.expiring_soon || 0}</p>
                <p className="text-sm text-gray-500">In Scadenza</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-300" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.legal_hold_count || 0}</p>
                <p className="text-sm text-gray-500">Legal Hold</p>
              </div>
              <Lock className="h-8 w-8 text-blue-300" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.by_status?.ARCHIVED || 0}</p>
                <p className="text-sm text-gray-500">Archiviati</p>
              </div>
              <Archive className="h-8 w-8 text-green-300" />
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="policies">
        <TabsList>
          <TabsTrigger value="policies">Policy Conservazione</TabsTrigger>
          <TabsTrigger value="expiring">Documenti in Scadenza</TabsTrigger>
        </TabsList>
        
        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Policy di Conservazione</CardTitle>
                  <CardDescription>
                    Definisci per quanto tempo conservare ogni categoria di documento
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {policies.length === 0 && (
                    <Button variant="outline" onClick={seedDefaults}>
                      <Shield className="h-4 w-4 mr-2" />
                      Carica Default
                    </Button>
                  )}
                  <Button onClick={openNewPolicyDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nuova Policy
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : policies.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Nessuna policy definita</p>
                  <p className="text-sm mt-2">Clicca "Carica Default" per iniziare con le policy standard</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Conservazione</TableHead>
                      <TableHead>Azione Scadenza</TableHead>
                      <TableHead>Legal Hold</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy) => (
                      <TableRow key={policy.id}>
                        <TableCell className="font-medium">
                          {getCategoryLabel(policy.category)}
                        </TableCell>
                        <TableCell>
                          {policy.retention_years 
                            ? `${policy.retention_years} anni`
                            : policy.retention_days 
                              ? `${policy.retention_days} giorni`
                              : 'Illimitata'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            policy.action_on_expiry === 'DELETE' ? 'destructive' :
                            policy.action_on_expiry === 'ANONYMIZE' ? 'secondary' : 'outline'
                          }>
                            {policy.action_on_expiry}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {policy.legal_hold_supported ? (
                            <Badge variant="outline" className="text-green-600">
                              <Lock className="h-3 w-3 mr-1" />
                              Supportato
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">
                          {policy.description || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditPolicyDialog(policy)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeletePolicy(policy.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Expiring Documents Tab */}
        <TabsContent value="expiring" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Documenti in Scadenza</CardTitle>
                  <CardDescription>
                    Documenti che scadranno nei prossimi 60 giorni
                  </CardDescription>
                </div>
                {selectedDocs.length > 0 && (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => handleBulkLegalHold(true)}>
                      <Lock className="h-4 w-4 mr-2" />
                      Applica Legal Hold ({selectedDocs.length})
                    </Button>
                    <Button variant="outline" onClick={() => handleBulkLegalHold(false)}>
                      <Unlock className="h-4 w-4 mr-2" />
                      Rimuovi Legal Hold
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!condoId ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-amber-300" />
                  <p>Seleziona un condominio per vedere i documenti in scadenza</p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : expiringDocs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-green-300" />
                  <p>Nessun documento in scadenza nei prossimi 60 giorni</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox 
                          checked={selectedDocs.length === expiringDocs.length}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedDocs(expiringDocs.map(d => d.id));
                            } else {
                              setSelectedDocs([]);
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead>Giorni Rimanenti</TableHead>
                      <TableHead>Azione</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiringDocs.map((doc) => {
                      const daysLeft = getDaysUntilExpiry(doc.expires_at);
                      return (
                        <TableRow key={doc.id}>
                          <TableCell>
                            <Checkbox 
                              checked={selectedDocs.includes(doc.id)}
                              onCheckedChange={() => toggleDocSelection(doc.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{doc.title}</div>
                            <div className="text-xs text-gray-500">{doc.document_code}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {getCategoryLabel(doc.category)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatDate(doc.expires_at)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              daysLeft <= 7 ? 'destructive' :
                              daysLeft <= 30 ? 'secondary' : 'outline'
                            }>
                              {daysLeft} giorni
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {doc.retention_action || 'ARCHIVE'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Policy Dialog */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPolicy ? 'Modifica Policy' : 'Nuova Policy di Conservazione'}
            </DialogTitle>
            <DialogDescription>
              Definisci il periodo di conservazione per una categoria di documenti
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Categoria documento</Label>
              <Select 
                value={policyForm.category} 
                onValueChange={(v) => setPolicyForm(f => ({ ...f, category: v }))}
                disabled={!!editingPolicy}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona categoria..." />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="years">Anni di conservazione</Label>
                <Input
                  id="years"
                  type="number"
                  min="0"
                  value={policyForm.retention_years}
                  onChange={(e) => setPolicyForm(f => ({ 
                    ...f, 
                    retention_years: e.target.value,
                    retention_days: '' // Clear days if years set
                  }))}
                  placeholder="Es. 10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="days">oppure Giorni</Label>
                <Input
                  id="days"
                  type="number"
                  min="0"
                  value={policyForm.retention_days}
                  onChange={(e) => setPolicyForm(f => ({ 
                    ...f, 
                    retention_days: e.target.value,
                    retention_years: '' // Clear years if days set
                  }))}
                  placeholder="Es. 365"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Azione alla scadenza</Label>
              <Select 
                value={policyForm.action_on_expiry} 
                onValueChange={(v) => setPolicyForm(f => ({ ...f, action_on_expiry: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RETENTION_ACTIONS.map(action => (
                    <SelectItem key={action.value} value={action.value}>
                      <div>
                        <div>{action.label}</div>
                        <div className="text-xs text-gray-500">{action.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="legal_hold"
                checked={policyForm.legal_hold_supported}
                onCheckedChange={(checked) => setPolicyForm(f => ({ ...f, legal_hold_supported: checked }))}
              />
              <Label htmlFor="legal_hold" className="text-sm">
                Supporta Legal Hold (blocco cancellazione per motivi legali)
              </Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Input
                id="description"
                value={policyForm.description}
                onChange={(e) => setPolicyForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Es. Conservazione obbligatoria per legge fiscale"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSavePolicy} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
